import React, { useEffect, useState, useRef, useCallback } from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  ArrowRight,
  LoaderCircle,
  XCircle,
  Cog,
  CheckCircle,
  Earth,
  AlertCircle,
  Paperclip,
  AudioLines,
  Plug,
  Maximize2,
  Minimize2
} from "lucide-react";
import { mergeTailwindClasses } from "../../helpers/tailwindClassHelper";
import useFileUpload, { getDisplayFileName } from "../../hooks/useFileUpload";
import { useInternet } from "../../context/InternetContext";
import { formatFileSize } from "../../helpers/fileSizeHelper";
import { Attach } from "../Message/MessageInputActions";

import { getPrompts, authenticateGoogleDrive, authenticateGoogleEmail, authenticateGoogleCalendar, authenticateOutlook } from "../../service/apiService";
import { useUser } from "../../context/UserContext";
import { useHat } from "../../context/HatContext";
import { useWorkflow } from "../../context/WorkflowContext";

import BrainBankSyncStrip from "../KnowledgeBase/BrainBankSyncStrip";
import FileIcon from "../Message/FileIcon";
import HatInput from "../Hat/HatInput";
import KnowledgeBaseInput from "../KnowledgeBase/KnowledgeBaseInput";
import WorkflowInput from "../Workflow/WorkflowInput";
import EnhancedAttach from "../Message/EnhancedAttach";
import GoogleDriveAuth from "../GoogleDrive/GoogleDriveAuth";
import GoogleDriveIcon from "../../icons/googleDrive";
import { getConnectorInfo, checkGoogleDriveConnection, checkGoogleEmailConnection, checkGoogleCalendarConnection, checkOutlookConnection } from "../../service/apiService";
import GoogleEmailAuth from "../GoogleEmail/GoogleEmailAuth";
import GoogleEmailIcon from "../../icons/googleEmail";
import GoogleCalendarAuth from "../GoogleCalendar/GoogleCalendarAuth";
import GoogleCalendarIcon from "../../icons/googleCalendar";
import OutlookAuth from "../Outlook/OutlookAuth";
import MicrosoftOutlookIcon from "../../icons/microsoftOutlook";
import DictateComponent from "../Dictate/DictateComponent";
import Tooltip from "../Tooltip/Tooltip";
import { useModel } from "../../context/ModelContext";
import { useFileTransfer } from "../../context/FileTransferContext";
import QuoteBlock from "../Chat/QuoteBlock";

// File name validation function
const validateFileName = (_fileName: string): boolean => {
  // Any filename is accepted. The backend stores the original name in S3 and
  // sanitizes only the label sent to Bedrock, so special characters, unicode and
  // emoji no longer need to be blocked on the client.
  return true;
};

// Function to create a valid filename suggestion
const createValidFileNameSuggestion = (originalName: string): string => {
  const lastDotIndex = originalName.lastIndexOf('.');
  let nameWithoutExt = originalName;
  let extension = '';

  if (lastDotIndex !== -1) {
    nameWithoutExt = originalName.substring(0, lastDotIndex);
    extension = originalName.substring(lastDotIndex);
  }

  // Replace consecutive spaces with a single space
  let validName = nameWithoutExt.replace(/\s+/g, ' ');

  // Replace any invalid characters with underscores
  validName = validName.replace(/[^a-zA-Z0-9\s\-()[\]_]/g, '_');

  return validName + extension;
};

interface CardData {
  id: string;
  category: string;
  short_prompt: string;
  long_prompt: string;
}

const EmptyChatMessageInput = ({
  sendMessage,
  handleMic,
  isRecording,
  isSpeaking,
  focusMode,
  setFocusMode,


  // Quote props
  quotedText = null,
  sourceMessageId = null,
  clearQuote = () => { },
}: {
  sendMessage: (message: string, files: File[], connector?: string, quotedText?: string, quotedMessageId?: string) => void;
  handleMic: () => void;
  isRecording: boolean;
  isSpeaking: boolean;
  focusMode: string;
  setFocusMode: (mode: string) => void;


  // Quote props
  quotedText?: string | null;
  sourceMessageId?: string | null;
  clearQuote?: () => void;
}) => {
  const {
    uploadedFiles,
    uploadProgress,
    fileStatusMap,
    uploading,
    removingFileIndex,
    handleFilesSelected,
    removeFile,
  } = useFileUpload();

  const { internetEnabled, toggleInternet } = useInternet();
  const { selectedHat, initialPromptSent, setInitialPromptSent } = useHat();
  const { getSelectedModelCapabilities } = useModel();


  const [message, setMessage] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const { userId, authenticated } = useUser();
  const [hoveredPrompt, setHoveredPrompt] = useState<string>("");

  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragError, setDragError] = useState<string>('');
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showGoogleDriveAuth, setShowGoogleDriveAuth] = useState<boolean>(false);
  const [showGoogleEmailAuth, setShowGoogleEmailAuth] = useState<boolean>(false);
  const [showGoogleCalendarAuth, setShowGoogleCalendarAuth] = useState<boolean>(false);
  const [showOutlookAuth, setShowOutlookAuth] = useState<boolean>(false);
  const [allConnectors, setAllConnectors] = useState<any[]>([]);
  const [showInvalidFileNameNotification, setShowInvalidFileNameNotification] = useState<boolean>(false);
  const [suggestedFileName, setSuggestedFileName] = useState<string>('');
  const [connectorsLoading, setConnectorsLoading] = useState(true);
  const [googleDriveConnected, setGoogleDriveConnected] = useState(false);
  const [googleEmailConnected, setGoogleEmailConnected] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const { registerFileHandler } = useFileTransfer();

  // Helper functions to check model capabilities
  const getModelCapabilities = () => {
    return getSelectedModelCapabilities();
  };

  const canUseVirtualAssistant = () => {
    const { tool_call_support } = getModelCapabilities();
    return tool_call_support;
  };

  const canUseFileUpload = () => {
    const { file_upload_support } = getModelCapabilities();
    return file_upload_support;
  };

  // Auto-populate message when selectedHat changes
  useEffect(() => {
    if (selectedHat?.initial_prompt && selectedHat.initial_prompt.trim() !== "" && !initialPromptSent) {
      setMessage(selectedHat.initial_prompt);
    }
  }, [selectedHat, initialPromptSent]);

  // Focus on mount only
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Voice Mode lives in ChatWindow, so it can stay open while the chat page
  // behind it fills with the conversation; the button just asks for it.
  const handleVoiceModeButtonClick = () => {
    window.dispatchEvent(new CustomEvent('openVoiceMode'));
  };



  // Balanced refocus logic - allows text selection but keeps cursor in box
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only proceed if textarea exists and is not currently focused
      if (!textareaRef.current || document.activeElement === textareaRef.current) {
        return;
      }

      const activeElement = document.activeElement;

      // Check if user is actively selecting text
      const selection = window.getSelection();
      const hasActiveSelection = selection && selection.toString().length > 0 && !selection.isCollapsed;

      // Don't refocus if user is actively selecting text
      if (hasActiveSelection) {
        return;
      }

      // Don't refocus if user is interacting with specific UI elements
      const isInteractingWithUI = activeElement && (
        activeElement.tagName === 'BUTTON' ||
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.closest('[role="dialog"]') ||
        activeElement.closest('[role="menu"]') ||
        activeElement.closest('.modal') ||
        activeElement.closest('[data-no-refocus]')
      );

      // Don't refocus if user is interacting with message content for reading/copying
      const isReadingContent = activeElement && (
        activeElement.closest('[class*="message"]') ||
        activeElement.closest('[class*="chat"]') ||
        activeElement.closest('[class*="response"]') ||
        activeElement.closest('pre') ||
        activeElement.closest('code')
      ) && hasActiveSelection; // Only avoid refocus if actually selecting text in content

      if (isInteractingWithUI || isReadingContent) {
        return;
      }

      // Refocus in most other cases to maintain cursor in box behavior
      textareaRef.current.focus();
    }, 150);

    return () => clearTimeout(timer);
  }, [uploadedFiles, message, isDragging, showInvalidFileNameNotification]);
  // Fetch connectors and check connection status
  useEffect(() => {
    let isMounted = true;

    const fetchConnectors = async () => {
      // Wait for proper authentication
      if (!userId || !authenticated) {
        if (isMounted) {
          setConnectorsLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setConnectorsLoading(true);
        }
        const response = await getConnectorInfo();

        if (isMounted) {
          setAllConnectors(response.connectors || []);
        }
      } catch (error) {
        console.error('Error fetching connectors:', error);
        if (isMounted) {
          setAllConnectors([]);
        }
      } finally {
        if (isMounted) {
          setConnectorsLoading(false);
        }
      }
    };

    fetchConnectors();

    return () => {
      isMounted = false;
    };
  }, [userId, authenticated]);

  // Check connection status for all services
  useEffect(() => {
    let isMounted = true;

    const checkConnectionStatus = async () => {
      if (!userId || !authenticated) return;

      try {
        const [driveConnected, emailConnected, calendarConnected, outlookConn] = await Promise.all([
          checkGoogleDriveConnection().catch(() => false),
          checkGoogleEmailConnection().catch(() => false),
          checkGoogleCalendarConnection().catch(() => false),
          checkOutlookConnection().catch(() => false)
        ]);

        if (isMounted) {
          setGoogleDriveConnected(driveConnected);
          setGoogleEmailConnected(emailConnected);
          setGoogleCalendarConnected(calendarConnected);
          setOutlookConnected(outlookConn);
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
      }
    };

    checkConnectionStatus();

    return () => {
      isMounted = false;
    };
  }, [userId, authenticated]);

  useEffect(() => {
    // Create a stable handler function that won't change on every render
    const fileHandler = (file: File) => {
      handleFilesSelected([file]);
    };

    // Register the stable handler
    registerFileHandler(fileHandler);

    // No cleanup needed as we want the handler to persist
  }, [handleFilesSelected, registerFileHandler]);

  // Models without file support: block drag & drop (dropEffect "none" also stops the browser opening the file)
  const fileUploadSupported = canUseFileUpload();

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileUploadSupported) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    setIsDragging(true);
  }, [fileUploadSupported]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileUploadSupported) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    if (!isDragging) setIsDragging(true);
  }, [isDragging, fileUploadSupported]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if we're leaving the drop area
    if (!dropAreaRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const validateFiles = useCallback((files: File[]) => {
    if (files.length === 0) return null;

    // Check for invalid file names
    const invalidFiles = files.filter(file => !validateFileName(file.name));

    if (invalidFiles.length > 0) {
      // Show the notification and set suggested name
      setShowInvalidFileNameNotification(true);

      // Generate a suggestion for the first invalid file
      const suggestion = createValidFileNameSuggestion(invalidFiles[0].name);
      setSuggestedFileName(suggestion);

      return `Invalid filename${invalidFiles.length > 1 ? 's' : ''}`;
    }

    return null;
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!fileUploadSupported) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const error = validateFiles(droppedFiles);

    if (error) {
      setDragError(error);
      setTimeout(() => setDragError(''), 3000);
      return;
    }

    // Filter valid files only
    const validFiles = droppedFiles.filter(file => validateFileName(file.name));
    if (validFiles.length > 0) {
      handleFilesSelected(validFiles);
    }
  }, [handleFilesSelected, validateFiles, fileUploadSupported]);

  useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragenter', preventDefaults);
    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('dragleave', preventDefaults);
    document.addEventListener('drop', preventDefaults);

    return () => {
      document.removeEventListener('dragenter', preventDefaults);
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('dragleave', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);



  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (message.trim() === "" && uploadedFiles.length === 0 && !quotedText) return;
    if (selectedHat?.initial_prompt) {
      setInitialPromptSent(true);
    }

    try {
      await Promise.all(
        uploadedFiles.map((file) =>
          uploadProgress[file.name] === 100 ||
            uploadProgress[file.name] === "failed"
            ? null
            : handleFilesSelected([file])
        )
      );

      sendMessage(message.trim(), uploadedFiles, undefined, quotedText || undefined, sourceMessageId || undefined);
      setMessage("");

      // Clear quote after sending
      if (quotedText && clearQuote) {
        clearQuote();
      }

      // Focus back to textarea after successful submission
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };
  const isAnyFileProcessing = uploadedFiles.some(
    (file) => fileStatusMap[file.name] === "processing"
  );



  // Handle manual message changes (user typing)
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  // Simple blur handler that doesn't aggressively refocus
  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Don't do anything on blur - let the user interact freely
    // The useEffect above will handle refocusing only when appropriate
  };
  // Handle Google Drive authentication
  const handleGoogleDriveAuth = () => {
    setShowGoogleDriveAuth(true);
  };

  const handleGoogleDriveAuthClose = () => {
    setShowGoogleDriveAuth(false);
  };

  const handleGoogleDriveAuthSuccess = async (code: string) => {
    try {
      console.log('Google Drive authorization code received:', code);

      const result = await authenticateGoogleDrive(code);

      if (result.success) {
        console.log('Google Drive connected successfully:', result.message);
        // Update connection status
        setTimeout(async () => {
          const connected = await checkGoogleDriveConnection().catch(() => false);
          setGoogleDriveConnected(connected);
        }, 500);
      } else {
        console.error('Failed to connect Google Drive:', result.error);
      }

      setShowGoogleDriveAuth(false);
    } catch (error) {
      console.error('Error connecting Google Drive:', error);
    }
  };

  const isGoogleDriveAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    const hasActiveGoogleDriveConnector = selectedConnectorIds.some(connectorId => {
      const connector = allConnectors.find(c => c.id === connectorId);
      return connector &&
        connector.status === 'active' &&
        connector.type === 'google-drive';
    });

    return hasActiveGoogleDriveConnector;
  };

  const isGmailAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    const hasActiveGmailConnector = selectedConnectorIds.some((connectorId: string) => {
      const connector = allConnectors.find((c: any) => c.id === connectorId);
      return connector &&
        connector.status === 'active' &&
        connector.type === 'google-email';
    });

    return hasActiveGmailConnector;
  };

  const isGoogleCalendarAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    const hasActiveGoogleCalendarConnector = selectedConnectorIds.some((connectorId: string) => {
      const connector = allConnectors.find((c: any) => c.id === connectorId);
      return connector &&
        connector.status === 'active' &&
        connector.type === 'google-calendar';
    });

    return hasActiveGoogleCalendarConnector;
  };

  const isOutlookAssistant = () => {
    if (!selectedHat || connectorsLoading || !allConnectors.length) return false;

    const hasConnectorsEnabled = selectedHat.feature_keys?.kyva_connectors?.enabled === true;
    if (!hasConnectorsEnabled) return false;

    const selectedConnectorIds = selectedHat.feature_keys?.kyva_connectors?.connector_ids || [];
    if (selectedConnectorIds.length === 0) return false;

    const hasActiveOutlookConnector = selectedConnectorIds.some((connectorId: string) => {
      const connector = allConnectors.find((c: any) => c.id === connectorId);
      return connector &&
        connector.status === 'active' &&
        connector.type === 'microsoft-outlook';
    });

    return hasActiveOutlookConnector;
  };

  // Handle Google Drive authentication
  const handleGoogleEmailAuth = () => {
    setShowGoogleEmailAuth(true);
  };

  const handleGoogleEmailAuthClose = () => {
    setShowGoogleEmailAuth(false);
  };

  const handleGoogleEmailAuthSuccess = async (code: string) => {
    try {
      console.log('Google Email authorization code received:', code);

      const result = await authenticateGoogleEmail(code);

      if (result.success) {
        console.log('Google Email connected successfully:', result.message);
        // Update connection status
        setTimeout(async () => {
          const connected = await checkGoogleEmailConnection().catch(() => false);
          setGoogleEmailConnected(connected);
        }, 500);
      } else {
        console.error('Failed to connect Google Email:', result.error);
      }

      setShowGoogleEmailAuth(false);
    } catch (error) {
      console.error('Error connecting Google Email:', error);
    }
  };

  // Handle Google Calendar authentication
  const handleGoogleCalendarAuth = () => {
    setShowGoogleCalendarAuth(true);
  };

  const handleGoogleCalendarAuthClose = () => {
    setShowGoogleCalendarAuth(false);
  };

  const handleGoogleCalendarAuthSuccess = async (code: string) => {
    try {
      console.log('Google Calendar authorization code received:', code);

      const result = await authenticateGoogleCalendar(code);

      if (result.success) {
        console.log('Google Calendar connected successfully:', result.message);
        // Update connection status
        setTimeout(async () => {
          const connected = await checkGoogleCalendarConnection().catch(() => false);
          setGoogleCalendarConnected(connected);
        }, 500);
      } else {
        console.error('Failed to connect Google Calendar:', result.error);
      }

      setShowGoogleCalendarAuth(false);
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
    }
  };

  // Handle Outlook authentication
  const handleOutlookAuth = () => {
    setShowOutlookAuth(true);
  };

  const handleOutlookAuthClose = () => {
    setShowOutlookAuth(false);
  };

  const handleOutlookAuthSuccess = async (code: string) => {
    try {
      const result = await authenticateOutlook(code);

      if (result.success) {
        console.log('Outlook connected successfully:', result.message);
        // Update connection status
        setTimeout(async () => {
          const connected = await checkOutlookConnection().catch(() => false);
          setOutlookConnected(connected);
        }, 500);
      } else {
        console.error('Failed to connect Outlook:', result.error);
      }

      setShowOutlookAuth(false);
    } catch (error) {
      console.error('Error connecting Outlook:', error);
    }
  };

  // Dictate handler
  const handleDictateTranscription = (text: string) => {
    setMessage(prev => prev + (prev ? " " : "") + text);
    // Focus back to textarea after transcription
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  return (
    <form
      ref={formRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onSubmit={handleFormSubmit}
      onKeyDown={(e) => {
        if (e.key === "Escape" && isExpanded) {
          e.preventDefault();
          setIsExpanded(false);
          setTimeout(() => textareaRef.current?.focus(), 10);
        }
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const isDisabled =
            (message.trim().length === 0 && uploadedFiles.length === 0 && !quotedText) ||
            uploading ||
            isAnyFileProcessing;
          if (!isDisabled) {
            handleFormSubmit(e);
          }
        }
      }}
      className="w-full max-w-2xl mx-auto"
    >
      <div className="flex flex-col bg-surface-overlay border border-border-strong px-5 pt-4 pb-3 rounded-2xl w-full shadow-sm relative text-primary focus-within:border-transparent focus-within:shadow-md focus-within:ring-1 focus-within:ring-violet-500/20 transition-all duration-200">
        {/* Global drag area that appears when files are dragged over the document */}
        {isDragging && (
          <div
            ref={dropAreaRef}
            className="absolute inset-0 z-50 flex items-center justify-center bg-violet-100/90 border-2 border-dashed border-violet-500 rounded-lg"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center bg-surface-overlay p-6 rounded-xl shadow-lg ring-1 ring-black/5 dark:ring-gray-700">
              <div className="mb-2">
                <Paperclip className="h-10 w-10 mx-auto text-violet-600 dark:text-violet-300" />
              </div>
              <p className="text-violet-800 dark:text-violet-200 font-medium text-lg">Drop files here</p>
              <p className="text-violet-600 dark:text-violet-300 text-sm">Drag and drop multiple files</p>
            </div>
          </div>
        )}

        {/* Error message */}
        {dragError && (
          <div className="absolute top-0 right-0 -mt-10 bg-red-100 text-red-700 px-3 py-1 rounded-lg flex items-center text-sm">
            <AlertCircle size={16} className="mr-1" />
            {dragError}
          </div>
        )}
        {/* Invalid filename notification */}
        {showInvalidFileNameNotification && (
          <div className="fixed bottom-[70px] left-0 right-0 mx-auto w-[calc(100%-16px)] max-w-2xl z-50 px-2 sm:px-4" style={{ animation: 'fadeIn 0.3s ease-out forwards' }}>
            <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-md p-2 sm:p-3 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="flex items-start sm:items-center space-x-2">
                  <AlertCircle className="text-amber-600 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-1 sm:mt-0.5" />
                  <p className="text-xs sm:text-sm text-amber-800">
                    File names can only contain alphanumeric characters, spaces, hyphens, parentheses,
                    and square brackets. No consecutive spaces allowed.
                  </p>
                </div>
                <button
                  onClick={() => setShowInvalidFileNameNotification(false)}
                  className="text-amber-600 hover:text-amber-800 rounded-full p-1 transition-colors ml-1 sm:ml-2 flex-shrink-0"
                  aria-label="Close notification"
                >
                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              {suggestedFileName && (
                <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm">
                  <p className="font-medium text-amber-800">Suggested filename:</p>
                  <div className="bg-white p-1 sm:p-1.5 rounded border border-amber-200 mt-1 text-gray-700 font-mono break-all text-xs sm:text-sm">
                    {suggestedFileName}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Quote Block */}
        {quotedText && (
          <div className="w-full mb-2">
            <QuoteBlock text={quotedText} onClear={clearQuote || (() => { })} />
          </div>
        )}
        <div className="relative flex-grow flex-shrink w-full group">
          <TextareaAutosize
            ref={textareaRef}
            value={hoveredPrompt || message}
            onChange={handleMessageChange}
            onBlur={handleTextareaBlur}
            minRows={2}
            className={mergeTailwindClasses(
              "bg-transparent placeholder:text-subtle text-[15px] leading-6 font-normal resize-none w-full focus:outline-none pr-8",
              isExpanded ? "!h-[50vh] !max-h-[50vh] overflow-y-auto" : "max-h-[120px] overflow-y-auto"
            )}
            placeholder="What's on your mind? Ask Kyva anything..."
            disabled={uploading}
          />
          <Tooltip label={isExpanded ? "Collapse (Esc)" : "Expand"} side="left">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsExpanded((prev) => !prev);
                setTimeout(() => textareaRef.current?.focus(), 10);
              }}
              className={mergeTailwindClasses(
                "absolute top-0 right-2 p-1 text-subtle hover:text-primary transition-opacity bg-surface-2 hover:bg-surface-3 rounded-md focus:outline-none",
                isExpanded ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              )}
            >
              {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </Tooltip>
        </div>

        {/* File upload section */}
        {uploadedFiles.length > 0 && (
          <div className="grid grid-cols-2 gap-2 my-2 w-full">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className="relative flex flex-col items-start overflow-hidden rounded-xl border border-border-default p-2 bg-surface-2"
              >
                <div className="relative flex items-center w-full">
                  <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md bg-surface-3 flex items-center justify-center text-xs">
                    <span className="text-primary text-[10px]">
                      <FileIcon file={{ file_name: file.name }} />
                    </span>
                  </div>
                  <div className="overflow-hidden flex-grow px-2">
                    <div className="truncate font-semibold text-xs pr-4">
                      {getDisplayFileName(file.name)}
                    </div>
                    <div className="truncate text-xs">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
                {uploadProgress[file.name] !== undefined && (
                  <div className="w-full flex items-center mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className={`${uploadProgress[file.name] === "failed"
                          ? "bg-red-500"
                          : "bg-green-500"
                          } h-1 rounded-full`}
                        style={{
                          width: `${uploadProgress[file.name] === "failed"
                            ? "100%"
                            : `${uploadProgress[file.name] || 0}%`
                            }`,
                        }}
                      ></div>
                    </div>
                    <div className="ml-2 text-xs text-gray-500 flex items-center gap-1">
                      {typeof uploadProgress[file.name] === "number"
                        ? `${(uploadProgress[file.name] as number).toFixed(2)}%`
                        : "ERROR"}
                      {fileStatusMap[file.name] === "processed" && <CheckCircle size={"12"} className="text-green-500" />}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="text-red-400 hover:text-red-600 absolute top-2 right-2 transform rounded-full border p-0.5 transition-colors hover:opacity-100"
                  onClick={() => removeFile(index)}
                  disabled={
                    removingFileIndex === index ||
                    fileStatusMap[file.name] === "processing"
                  }
                >
                  {removingFileIndex === index ? (
                    <LoaderCircle
                      size={"17"}
                      className="animate-spin text-red-600"
                    />
                  ) : fileStatusMap[file.name] === "processing" ? (
                    <Cog size={"17"} className="animate-spin text-blue-500" />
                  ) : (
                    <XCircle size={"17"} />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
        <BrainBankSyncStrip hasAttachments={uploadedFiles.length > 0} />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 pt-3 border-t border-border-default space-y-2 sm:space-y-0">
          <div className="flex flex-row items-center space-x-1 -mx-2"></div>

          {/* Desktop: All buttons in one row */}
          <div className="hidden sm:flex flex-row items-center space-x-4 -mx-2">
            {/* Dictate button - records and transcribes speech */}
            <DictateComponent
              onTranscription={handleDictateTranscription}
              disabled={uploading || isAnyFileProcessing}
            />

            <Tooltip label="Start voice mode" side="top">
              <button
                type="button"
                onClick={handleVoiceModeButtonClick}
                className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-all duration-200"
              >
                <AudioLines size={17} />
              </button>
            </Tooltip>
            {/* Gmail button with connection status indicator */}
            {canUseVirtualAssistant() && isGmailAssistant() && (
              <Tooltip label={googleEmailConnected ? "Gmail connected" : "Gmail not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleEmailAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleEmailIcon size={18} />
                  {!googleEmailConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}
            {/* Google Calendar button with connection status indicator */}
            {canUseVirtualAssistant() && isGoogleCalendarAssistant() && (
              <Tooltip label={googleCalendarConnected ? "Google Calendar connected" : "Google Calendar not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleCalendarAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleCalendarIcon size={18} />
                  {!googleCalendarConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}
            {/* Outlook button with connection status indicator */}
            {canUseVirtualAssistant() && isOutlookAssistant() && (
              <Tooltip label={outlookConnected ? "Outlook connected" : "Outlook not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleOutlookAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <MicrosoftOutlookIcon size={18} />
                  {!outlookConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}
            {/* Google Drive button with connection status indicator */}
            {canUseVirtualAssistant() && isGoogleDriveAssistant() && (
              <Tooltip label={googleDriveConnected ? "Google Drive connected" : "Google Drive not connected — Click to connect"} side="top">
                <button
                  type="button"
                  onClick={handleGoogleDriveAuth}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                >
                  <GoogleDriveIcon size={18} />
                  {!googleDriveConnected && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                      <Plug className="text-white" size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              </Tooltip>
            )}
            <Tooltip label={internetEnabled ? "Disable internet access" : "Enable internet access"} side="top">
              <button
                type="button"
                onClick={toggleInternet}
                className={`rounded-full p-2 transition-colors duration-200 ${internetEnabled
                  ? "bg-violet-600 text-white"
                  : "bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary"
                  }`}
              >
                <Earth size={17} />
              </button>
            </Tooltip>
            {canUseVirtualAssistant() && <HatInput title="Virtual Assistant" />}
            {canUseVirtualAssistant() && <KnowledgeBaseInput title="BrainBank" />}
            <WorkflowInput title="Workflow" />
            {canUseFileUpload() && <EnhancedAttach onFilesSelected={handleFilesSelected} title="Upload files" />}
            <button
              disabled={
                (message.trim().length === 0) ||
                uploading ||
                isAnyFileProcessing
              }
              className="bg-gradient-to-br from-violet-600 to-violet-700 text-white disabled:text-subtle hover:from-violet-700 hover:to-violet-800 disabled:bg-none disabled:bg-surface-3 disabled:cursor-not-allowed transition-all duration-150 rounded-full inline-flex items-center justify-center text-sm font-medium h-9 w-9"
            >
              <ArrowRight className="bg-background" size={17} />
            </button>
          </div>

          {/* Mobile: First row - Main actions (mirrors desktop order) */}
          <div className="flex sm:hidden flex-row items-center justify-between -mx-2">
            <div className="flex flex-row items-center space-x-4">
              {/* Dictate button - records and transcribes speech */}
              <DictateComponent
                onTranscription={handleDictateTranscription}
                disabled={uploading || isAnyFileProcessing}
              />

              {/* Voice mode button - opens voice mode modal */}
              <Tooltip label="Start voice mode" side="top">
                <button
                  type="button"
                  onClick={handleVoiceModeButtonClick}
                  className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-all duration-200"
                >
                  <AudioLines size={17} />
                </button>
              </Tooltip>

              {/* Gmail button with connection status indicator */}
              {canUseVirtualAssistant() && isGmailAssistant() && (
                <Tooltip label={googleEmailConnected ? "Gmail connected" : "Gmail not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleEmailAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleEmailIcon size={18} />
                    {!googleEmailConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {/* Google Calendar button with connection status indicator */}
              {canUseVirtualAssistant() && isGoogleCalendarAssistant() && (
                <Tooltip label={googleCalendarConnected ? "Google Calendar connected" : "Google Calendar not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleCalendarAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleCalendarIcon size={18} />
                    {!googleCalendarConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {/* Outlook button with connection status indicator */}
              {canUseVirtualAssistant() && isOutlookAssistant() && (
                <Tooltip label={outlookConnected ? "Outlook connected" : "Outlook not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleOutlookAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <MicrosoftOutlookIcon size={18} />
                    {!outlookConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              {/* Google Drive button with connection status indicator */}
              {canUseVirtualAssistant() && isGoogleDriveAssistant() && (
                <Tooltip label={googleDriveConnected ? "Google Drive connected" : "Google Drive not connected — Click to connect"} side="top">
                  <button
                    type="button"
                    onClick={handleGoogleDriveAuth}
                    className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary rounded-full p-2 transition-colors duration-200 relative"
                  >
                    <GoogleDriveIcon size={18} />
                    {!googleDriveConnected && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full p-0.5">
                        <Plug className="text-white" size={10} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                </Tooltip>
              )}

              <Tooltip label={internetEnabled ? "Disable internet access" : "Enable internet access"} side="top">
                <button
                  type="button"
                  onClick={toggleInternet}
                  className={`rounded-full p-2 transition-colors duration-200 ${internetEnabled
                    ? "bg-violet-600 text-white"
                    : "bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary"
                    }`}
                >
                  <Earth size={17} />
                </button>
              </Tooltip>
              {canUseVirtualAssistant() && <HatInput title="Virtual Assistant" />}
              {canUseVirtualAssistant() && <KnowledgeBaseInput title="BrainBank" />}
              <WorkflowInput title="Workflow" />
              {canUseFileUpload() && <EnhancedAttach onFilesSelected={handleFilesSelected} title="Upload files" />}
            </div>

            <button
              disabled={
                (message.trim().length === 0) ||
                uploading ||
                isAnyFileProcessing
              }
              className="bg-gradient-to-br from-violet-600 to-violet-700 text-white disabled:text-subtle hover:from-violet-700 hover:to-violet-800 disabled:bg-none disabled:bg-surface-3 disabled:cursor-not-allowed transition-all duration-150 rounded-full inline-flex items-center justify-center text-sm font-medium h-9 w-9"
            >
              <ArrowRight className="bg-background" size={17} />
            </button>
          </div>
        </div>
      </div>
      {/* Google Drive Authentication Modal */}
      <GoogleDriveAuth
        isOpen={showGoogleDriveAuth}
        onClose={handleGoogleDriveAuthClose}
        onAuthSuccess={handleGoogleDriveAuthSuccess}
      />
      {/* Google Email Authentication Modal */}
      <GoogleEmailAuth
        isOpen={showGoogleEmailAuth}
        onClose={handleGoogleEmailAuthClose}
        onAuthSuccess={handleGoogleEmailAuthSuccess}
      />

      <GoogleCalendarAuth
        isOpen={showGoogleCalendarAuth}
        onClose={handleGoogleCalendarAuthClose}
        onAuthSuccess={handleGoogleCalendarAuthSuccess}
      />

      {/* Outlook Authentication Modal */}
      <OutlookAuth
        isOpen={showOutlookAuth}
        onClose={handleOutlookAuthClose}
        onAuthSuccess={handleOutlookAuthSuccess}
      />

    </form>
  );
};

export default EmptyChatMessageInput;